import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../../utils/api';

const STEPS = ['business', 'connect', 'subscription'];

export default function Onboarding() {
  const { getToken } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchMe = useCallback(async () => {
    const res = await api('/api/owners/me', { getToken });
    if (!res.ok) {
      if (res.status === 403) {
        navigate('/', { replace: true });
        return null;
      }
      setError('Failed to load');
      return null;
    }
    const d = await res.json();
    setData(d);
    setError(null);
    return d;
  }, [getToken, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await fetchMe();
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [fetchMe]);

  // After Connect return or subscription success, refetch (webhook may be delayed)
  useEffect(() => {
    const returnParam = searchParams.get('return');
    const successParam = searchParams.get('success');
    const sessionId = searchParams.get('session_id');
    if (returnParam !== '1' && successParam !== '1') return;

    let pollCount = 0;
    const maxPolls = 15; // ~30 seconds
    let cancelled = false;

    const poll = async () => {
      // If we have session_id from Stripe success redirect, confirm subscription first (fallback when webhook is delayed)
      if (successParam === '1' && sessionId && pollCount === 0) {
        try {
          await api('/api/owners/me/confirm-subscription', {
            getToken,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId }),
          });
        } catch (_) { /* ignore; webhook may have already updated */ }
      }

      const d = await fetchMe();
      if (cancelled || !d?.business) return;
      const connectDone = !!d.business.stripeAccountId;
      const subDone = ['trialing', 'active'].includes(d.business.subscriptionStatus || '');
      if (subDone && connectDone) {
        navigate('/owner/dashboard', { replace: true });
        return true;
      }
      if (connectDone && returnParam === '1' && !successParam) return true; // Connect done, stop polling
      return false;
    };

    const timer = setInterval(async () => {
      if (cancelled) return;
      pollCount++;
      const done = await poll();
      if (done || pollCount >= maxPolls) clearInterval(timer);
    }, 2000);
    // Run once immediately for faster redirect when session_id confirms
    poll().then((done) => { if (done) clearInterval(timer); });

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [searchParams, fetchMe, navigate, getToken]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <p className="text-slate-600 dark:text-slate-400">Loading…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-6">
        <p className="text-red-700 dark:text-red-300">{error || 'Failed to load'}</p>
      </div>
    );
  }

  const { business } = data;
  const step1Done = business?.name && business?.slug && !/^b-[a-f0-9]{12}$/.test(business.slug || '');
  const step2Done = !!business?.stripeAccountId;
  const step3Done = ['trialing', 'active'].includes(business?.subscriptionStatus || '');

  if (step1Done && step2Done && step3Done) {
    navigate('/owner/dashboard', { replace: true });
    return null;
  }

  const currentStep = !step1Done ? 'business' : !step2Done ? 'connect' : 'subscription';

  return (
    <div className="max-w-xl mx-auto space-y-8">
      <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
        Set up your business
      </h1>

      {/* Progress */}
      <div className="flex gap-2">
        {STEPS.map((s, i) => (
          <div
            key={s}
            className={`h-2 flex-1 rounded ${
              STEPS.indexOf(currentStep) >= i
                ? 'bg-amber-500'
                : 'bg-slate-200 dark:bg-slate-700'
            }`}
          />
        ))}
      </div>

      {currentStep === 'business' && (
        <StepBusiness
          business={business}
          onSaved={fetchMe}
          saving={saving}
          setSaving={setSaving}
          setError={setError}
          getToken={getToken}
        />
      )}

      {currentStep === 'connect' && (
        <StepConnect
          onConnect={async () => {
            setSaving(true);
            setError(null);
            try {
              const res = await api('/api/owners/me/connect-account-link', {
                getToken,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: '{}',
              });
              const d = await res.json();
              if (!res.ok) throw new Error(d.error || `Error ${res.status}`);
              if (d.url) window.location.href = d.url;
              else throw new Error('No URL returned');
            } catch (err) {
              setError(err.message);
            } finally {
              setSaving(false);
            }
          }}
          loading={saving}
        />
      )}

      {currentStep === 'subscription' && (
        <StepSubscription
          onSubscribe={async () => {
            setSaving(true);
            setError(null);
            try {
              const res = await api('/api/owners/me/create-subscription', {
                getToken,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: '{}',
              });
              const d = await res.json();
              if (!res.ok) throw new Error(d.error || `Error ${res.status}`);
              if (d.url) window.location.href = d.url;
              else throw new Error('No URL returned');
            } catch (err) {
              setError(err.message);
            } finally {
              setSaving(false);
            }
          }}
          loading={saving}
        />
      )}

      {error && (
        <div className="rounded-lg bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4">
          {error}
        </div>
      )}
    </div>
  );
}

function StepBusiness({ business, onSaved, saving, setSaving, setError, getToken }) {
  const [name, setName] = useState(business?.name || '');

  useEffect(() => {
    setName(business?.name || '');
  }, [business?.name]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api('/api/owners/me/business', {
        getToken,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || d.detail || `Error ${res.status}`);
      await onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSave} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Business name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Tacos on Wheels"
          className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
        />
        <p className="text-xs text-slate-500 mt-1">
          Your menu URL will be generated from the name (no spaces). e.g. &quot;Tacos on Wheels&quot; → /m/tacsonwheels
        </p>
      </div>
      {business?.slug && (
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Menu URL: /m/{business.slug}
        </p>
      )}
      <button
        type="submit"
        disabled={saving || !name.trim()}
        className="px-6 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-900 font-medium"
      >
        {saving ? 'Saving…' : 'Save & continue'}
      </button>
    </form>
  );
}

function StepConnect({ onConnect, loading }) {
  return (
    <div className="space-y-4">
      <p className="text-slate-600 dark:text-slate-400">
        Connect your Stripe account to receive payments from customers.
      </p>
      <button
        type="button"
        onClick={onConnect}
        disabled={loading}
        className="px-6 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-900 font-medium"
      >
        {loading ? 'Redirecting…' : 'Connect your account'}
      </button>
    </div>
  );
}

function StepSubscription({ onSubscribe, loading }) {
  return (
    <div className="space-y-4">
      <p className="text-slate-600 dark:text-slate-400">
        Start your 90-day free trial. After trial, $10/month. Payment method required at signup.
      </p>
      <button
        type="button"
        onClick={onSubscribe}
        disabled={loading}
        className="px-6 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-900 font-medium"
      >
        {loading ? 'Redirecting…' : 'Start 90-day free trial'}
      </button>
    </div>
  );
}
